
import React, { useState, useMemo } from 'react';
import { StaffMember, Schedule, Shift, AttendanceRequest, AttendanceArchive } from '../types';
import { Icons } from './ui/Icons';
import { formatDateYYYYMMDD, getWeekStartDate, calculateHours } from '../utils/dateUtils';

interface AttendanceViewProps {
    staffList: StaffMember[];
    schedule: Schedule;
    shifts: Shift[];
    attendanceRequests: AttendanceRequest[];
    attendanceArchives?: AttendanceArchive[];
    onReviewRequest: (id: string, status: 'APPROVED' | 'REJECTED', adminNote?: string) => void;
    onInitiatePrint: (periodStart: string, periodEnd: string) => void;
    onArchivePayroll?: () => void;
}

const AttendanceView: React.FC<AttendanceViewProps> = ({
    staffList,
    schedule,
    shifts,
    attendanceRequests,
    attendanceArchives = [],
    onReviewRequest,
    onInitiatePrint,
    onArchivePayroll
}) => {
    const [periodStart, setPeriodStart] = useState<string>(formatDateYYYYMMDD(getWeekStartDate(new Date(), 1))); // Default to this week Monday
    const [showHistory, setShowHistory] = useState(false);
    const [showArchive, setShowArchive] = useState(false);
    const [reviewNotes, setReviewNotes] = useState<{[key: string]: string}>({});

    const processedRequests = useMemo(() => {
        return attendanceRequests.map(req => {
            const staff = staffList.find(s => s.id === req.employeeId);
            const shiftTemplate = shifts.find(s => s.id === req.shiftId);
            
            // Use snapshots if available from schedule (though requests should have originalStartTime/EndTime)
            const scheduledHours = calculateHours(req.originalStartTime, req.originalEndTime);
            const actualHours = calculateHours(req.requestedStartTime, req.requestedEndTime);
            const diff = Math.round((actualHours - scheduledHours) * 100) / 100;

            return {
                ...req,
                staffName: staff?.name || `Unknown (${req.employeeId})`,
                shiftName: shiftTemplate?.name || 'Assigned',
                scheduledHours,
                actualHours,
                diff
            };
        }).sort((a, b) => b.date.localeCompare(a.date));
    }, [attendanceRequests, staffList, shifts]);

    const pendingRequests = useMemo(() => 
        processedRequests.filter(r => r.status === 'PENDING'), 
    [processedRequests]);

    const historyRequests = useMemo(() => 
        processedRequests.filter(r => r.status !== 'PENDING'), 
    [processedRequests]);

    const processedArchives = useMemo(() => {
        return attendanceArchives.map(archive => {
            const staff = staffList.find(s => s.id === archive.employeeId);
            const scheduledHours = calculateHours(archive.originalTimeIn, archive.originalTimeOut);
            const actualHours = calculateHours(archive.requestedTimeIn, archive.requestedTimeOut);
            
            return {
                ...archive,
                staffName: staff?.name || `Unknown (${archive.employeeId})`,
                scheduledHours,
                actualHours
            };
        }).sort((a, b) => b.archivedAt.localeCompare(a.archivedAt));
    }, [attendanceArchives, staffList]);

    const handlePrint = () => {
        const startDate = new Date(periodStart);
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 13); // 14 days total
        const endStr = formatDateYYYYMMDD(endDate);

        onInitiatePrint(periodStart, endStr); 
    };

    const handleNoteChange = (id: string, note: string) => {
        setReviewNotes(prev => ({ ...prev, [id]: note }));
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-slate-800 p-6 rounded-[32px] shadow-sm border border-gray-100 dark:border-slate-800">
                <div>
                    <h2 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tight">Attendance Viewer</h2>
                    <p className="text-gray-500 dark:text-gray-400 font-medium">Live view of staff time adjustments and pending approvals.</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex flex-col">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 px-1">Print From</label>
                        <input 
                            type="date" 
                            value={periodStart}
                            onChange={(e) => setPeriodStart(e.target.value)}
                            className="px-4 py-2 bg-gray-50 dark:bg-slate-900 border-none rounded-xl text-xs font-bold focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                    <button 
                        onClick={handlePrint}
                        className="px-6 py-3 bg-indigo-600 text-white text-xs font-black uppercase tracking-widest rounded-2xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-100 dark:shadow-none flex items-center gap-2 mt-auto"
                    >
                        <Icons.Printer className="w-4 h-4" />
                        Print Sheet
                    </button>
                </div>
            </div>

            {/* Pending Requests Section */}
            <div className="bg-white dark:bg-slate-800 rounded-[32px] shadow-sm border border-gray-100 dark:border-slate-800 overflow-hidden">
                <div className="p-6 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between bg-amber-50/30 dark:bg-amber-900/10">
                    <h3 className="font-black text-gray-900 dark:text-white uppercase tracking-widest flex items-center gap-3">
                        <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center text-amber-600">
                            <Icons.Clock className="w-5 h-5 animate-pulse" />
                        </div>
                        Pending Requests ({pendingRequests.length})
                    </h3>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50/50 dark:bg-slate-900/50">
                                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Employee</th>
                                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Shift Target</th>
                                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Actual Time</th>
                                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Variance</th>
                                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Reason / Note</th>
                                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest">Feedback</th>
                                <th className="px-6 py-5 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                            {pendingRequests.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest text-xs italic">
                                        All caught up! No pending requests.
                                    </td>
                                </tr>
                            ) : (
                                pendingRequests.map((req) => (
                                    <tr key={req.id} className="hover:bg-gray-50 dark:hover:bg-slate-900/30 transition-colors">
                                        <td className="px-6 py-5">
                                            <div className="flex flex-col">
                                                <span className="font-black text-gray-900 dark:text-white uppercase tracking-tight">{req.staffName}</span>
                                                <span className="text-[10px] text-gray-400 font-bold tracking-widest">{req.date}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 text-center">
                                            <div className="inline-flex flex-col px-3 py-1 bg-gray-100 dark:bg-slate-700/50 rounded-xl">
                                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-tight">{req.shiftName}</span>
                                                <span className="text-xs font-bold text-gray-600 dark:text-gray-300">{req.originalStartTime} - {req.originalEndTime}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 text-center">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-black text-indigo-600 dark:text-indigo-400">{req.requestedStartTime} - {req.requestedEndTime}</span>
                                                <span className="text-[10px] font-bold text-gray-400 uppercase">{req.actualHours}h total</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5 text-center">
                                            <span className={`px-2 py-1 rounded-lg text-[10px] font-black ${req.diff > 0 ? 'bg-amber-100 text-amber-700' : req.diff < 0 ? 'bg-rose-100 text-rose-700' : 'bg-gray-100 text-gray-500'}`}>
                                                {req.diff > 0 ? `+${req.diff}` : req.diff}H
                                            </span>
                                        </td>
                                        <td className="px-6 py-5 max-w-[200px]">
                                            <p className="text-xs text-gray-600 dark:text-gray-400 italic leading-relaxed" title={req.staffNote}>
                                                {req.staffNote || 'No reason provided'}
                                            </p>
                                        </td>
                                        <td className="px-6 py-5">
                                            <input 
                                                type="text"
                                                placeholder="Approval note..."
                                                value={reviewNotes[req.id || ''] || ''}
                                                onChange={(e) => handleNoteChange(req.id || '', e.target.value)}
                                                className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-900 border-none rounded-xl text-xs font-bold focus:ring-2 focus:ring-indigo-500 outline-none shadow-inner"
                                            />
                                        </td>
                                        <td className="px-6 py-5 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button 
                                                    onClick={() => onReviewRequest(req.id!, 'APPROVED', reviewNotes[req.id!])}
                                                    className="w-10 h-10 flex items-center justify-center bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 rounded-2xl hover:bg-emerald-600 hover:text-white transition-all group shadow-sm"
                                                    title="Approve Adjustment"
                                                >
                                                    <Icons.CheckCircle className="w-6 h-6 transform group-hover:scale-110 transition-transform" />
                                                </button>
                                                <button 
                                                    onClick={() => onReviewRequest(req.id!, 'REJECTED', reviewNotes[req.id!])}
                                                    className="w-10 h-10 flex items-center justify-center bg-rose-50 dark:bg-rose-900/30 text-rose-600 rounded-2xl hover:bg-rose-600 hover:text-white transition-all group shadow-sm"
                                                    title="Reject & Keep Original"
                                                >
                                                    <Icons.XCircle className="w-6 h-6 transform group-hover:scale-110 transition-transform" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* History Toggle */}
            <div className="flex justify-center gap-4">
                <button 
                    onClick={() => setShowHistory(!showHistory)}
                    className="flex items-center gap-2 px-6 py-2 bg-white dark:bg-slate-800 text-gray-500 hover:text-indigo-600 font-bold text-xs uppercase tracking-widest rounded-full border border-gray-100 dark:border-slate-700 shadow-sm transition-all"
                >
                    {showHistory ? <Icons.ChevronUp className="w-4 h-4" /> : <Icons.History className="w-4 h-4" />}
                    {showHistory ? 'Hide Review History' : 'Show Review History'}
                </button>
                <button 
                    onClick={() => setShowArchive(!showArchive)}
                    className="flex items-center gap-2 px-6 py-2 bg-white dark:bg-slate-800 text-gray-500 hover:text-emerald-600 font-bold text-xs uppercase tracking-widest rounded-full border border-gray-100 dark:border-slate-700 shadow-sm transition-all"
                >
                    {showArchive ? <Icons.ChevronUp className="w-4 h-4" /> : <Icons.Archive className="w-4 h-4" />}
                    {showArchive ? 'Hide Archive' : 'Show Archive'}
                </button>
                {onArchivePayroll && (
                    <button 
                        onClick={onArchivePayroll}
                        className="flex items-center gap-2 px-6 py-2 bg-rose-50 dark:bg-rose-900/10 text-rose-600 font-bold text-xs uppercase tracking-widest rounded-full border border-rose-100 dark:border-rose-900/30 shadow-sm transition-all hover:bg-rose-100"
                    >
                        <Icons.Lock className="w-4 h-4" />
                        Close Payroll Period
                    </button>
                )}
            </div>

            {showHistory && (
                <div className="bg-white dark:bg-slate-800 rounded-[32px] shadow-sm border border-gray-100 dark:border-slate-800 overflow-hidden animate-in fade-in slide-in-from-top-4 duration-300">
                    <div className="p-6 border-b border-gray-100 dark:border-slate-700">
                        <h3 className="font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest text-xs flex items-center gap-2">
                            <Icons.History className="w-4 h-4" />
                            Processed Requests History
                        </h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50/50 dark:bg-slate-900/50">
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Employee</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Date</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Adjusted Time</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">Result</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Admin Response</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                                {historyRequests.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-8 text-center text-gray-400 italic text-xs font-bold uppercase">No history found.</td>
                                    </tr>
                                ) : (
                                    historyRequests.map((req) => (
                                        <tr key={req.id} className="opacity-70 grayscale-[0.5] hover:grayscale-0 hover:opacity-100 transition-all">
                                            <td className="px-6 py-4 text-xs font-black text-gray-900 dark:text-white uppercase tracking-tight">{req.staffName}</td>
                                            <td className="px-6 py-4 text-[10px] font-bold text-gray-500 uppercase">{req.date}</td>
                                            <td className="px-6 py-4 text-xs font-bold text-gray-600 dark:text-gray-400">{req.requestedStartTime} - {req.requestedEndTime}</td>
                                            <td className="px-6 py-4 text-center">
                                                <span className={`px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-widest ${
                                                    req.status === 'APPROVED' ? 'bg-emerald-100/50 text-emerald-700' : 'bg-rose-100/50 text-rose-700'
                                                }`}>
                                                    {req.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-xs text-gray-500 dark:text-gray-400">
                                                {req.adminNote || '-'}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {showArchive && (
                <div className="bg-white dark:bg-slate-800 rounded-[32px] shadow-sm border border-gray-100 dark:border-slate-800 overflow-hidden animate-in fade-in slide-in-from-top-4 duration-300">
                    <div className="p-6 border-b border-gray-100 dark:border-slate-700 bg-emerald-50/20 dark:bg-emerald-900/5">
                        <h3 className="font-black text-emerald-600 uppercase tracking-widest text-xs flex items-center gap-2">
                            <Icons.Archive className="w-4 h-4" />
                            Archived Attendance Records
                        </h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50/50 dark:bg-slate-900/50">
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Employee</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Work Date</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Baseline</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Final Time</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Archived At</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                                {processedArchives.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-8 text-center text-gray-400 italic text-xs font-bold uppercase">No archived records.</td>
                                    </tr>
                                ) : (
                                    processedArchives.map((archive) => (
                                        <tr key={archive.id} className="text-xs hover:bg-gray-50 dark:hover:bg-slate-900/30">
                                            <td className="px-6 py-4 font-black uppercase tracking-tight text-gray-900 dark:text-white">{archive.staffName}</td>
                                            <td className="px-6 py-4 font-bold text-gray-500 uppercase">{archive.date}</td>
                                            <td className="px-6 py-4 text-gray-500">{archive.originalTimeIn} - {archive.originalTimeOut}</td>
                                            <td className="px-6 py-4 font-bold text-gray-900 dark:text-white">{archive.requestedTimeIn} - {archive.requestedTimeOut}</td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-0.5 rounded-lg text-[10px] font-black uppercase tracking-widest ${
                                                    archive.finalStatus === 'APPROVED' ? 'bg-emerald-100/50 text-emerald-700' : 'bg-rose-100/50 text-rose-700'
                                                }`}>
                                                    {archive.finalStatus}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-gray-400 text-[10px]">{new Date(archive.archivedAt).toLocaleString()}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AttendanceView;
